import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CartStore } from './cart.store';
import { SeoService } from '../seo/seo.service';
import { environment } from '../../environments/environment';
import { CategoryNavComponent } from '../categories/category-nav.component';

interface OrderForm {
  name: string;
  phone: string;
  comment: string;
}

@Component({
  selector: 'app-cart',
  imports: [RouterModule, DecimalPipe, FormsModule, MatSnackBarModule, CategoryNavComponent],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
})
export class CartComponent implements OnInit {
  readonly cart   = inject(CartStore);
  private readonly snackBar = inject(MatSnackBar);
  private readonly seo = inject(SeoService);

  ngOnInit(): void {
    this.seo.setCart();
  }

  form: OrderForm = { name: '', phone: '', comment: '' };
  submitted  = signal(false);
  submitting = signal(false);

  changeQty(productId: string, weightLabel: string | null, delta: number): void {
    const item = this.cart.items().find(
      i => i.productId === productId && i.weightLabel === weightLabel,
    );
    if (item) {
      this.cart.updateQty(productId, weightLabel, item.quantity + delta);
    }
  }

  async submitOrder(): Promise<void> {
    if (!this.form.name.trim() || !this.form.phone.trim()) return;

    this.submitting.set(true);

    try {
      const res = await fetch(`${environment.cartWorkerUrl}/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: {
            name:    this.form.name.trim(),
            phone:   this.form.phone.trim(),
            comment: this.form.comment.trim(),
          },
          items: this.cart.items().map(({ imageUrl: _, ...item }) => ({
            ...item,
            productUrl: `${location.origin}/product/${item.sku}`,
          })),
          total: this.cart.totalPrice(),
        }),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      const data = await res.json() as { sheetsError?: string | null };
      if (data.sheetsError) {
        this.snackBar.open(
          'Замовлення прийнято, але виникла помилка при записі. Зв\'яжіться з нами.',
          'OK',
          { duration: 6000, panelClass: 'snack-warning' },
        );
      }

      this.submitted.set(true);
      this.cart.clear();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Помилка з\'єднання. Спробуйте ще раз.';
      this.snackBar.open(message, 'OK', {
        duration: 5000,
        panelClass: 'snack-error',
      });
    } finally {
      this.submitting.set(false);
    }
  }
}
